import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnsMenu } from "@/components/ui/columns-menu";
import { SearchInput } from "@/components/ui/search-input";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableStateRow } from "@/components/ui/table-state-row";
import { useToast } from "@/components/ui/use-toast";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { useDebounce } from "@/hooks/use-debounce";
import { useUrlListState } from "@/hooks/use-url-list-state";
import { diseasesAPI } from "@/lib/diseases-api";
import { queryKeys } from "@/lib/query-keys";
import { DiseaseTableRow } from "./DiseaseTableRow";

type DiseaseColumnKey = "short_name" | "icd10_code" | "status" | "flags";

const DISEASE_COLUMNS: Array<{ key: DiseaseColumnKey; label: string }> = [
  { key: "short_name", label: "Short Name" },
  { key: "icd10_code", label: "ICD-10" },
  { key: "status", label: "Status" },
  { key: "flags", label: "Flags" },
];

interface DiseaseListViewProps {
  onView: (diseaseId: string) => void;
  onAdd: () => void;
}

export const DiseaseListView = ({ onView, onAdd }: DiseaseListViewProps) => {
  const { toast } = useToast();
  // Search, filters and page live in the URL, so coming back from a disease
  // lands on the same results instead of the full list.
  const [listState, setListState] = useUrlListState({
    q: "",
    page: 1,
    prevalent: false,
    family: false,
    featured: false,
  });
  const searchInput = listState.q;
  const currentPage = Math.max(listState.page, 1);
  const widelyPrevalentOnly = listState.prevalent;
  const familyLinkedOnly = listState.family;
  const featuredOnly = listState.featured;
  const setSearchInput = (q: string) => setListState({ q });
  const setCurrentPage = (page: number) => setListState({ page });
  const { visible: visibleColumns, toggle: toggleColumn } =
    useColumnVisibility<DiseaseColumnKey>(DISEASE_COLUMNS.map((c) => c.key));
  const debouncedSearch = useDebounce(searchInput, 350);
  const pageSize = 20;

  const params = {
    page: currentPage,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    widely_prevalent: widelyPrevalentOnly || undefined,
    family_linked: familyLinkedOnly || undefined,
    is_featured: featuredOnly || undefined,
  };

  const { data, isLoading, isError, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.diseases.list(params),
    queryFn: () => diseasesAPI.list(params),
    retry: false,
  });

  useEffect(() => {
    if (!isError) return;
    toast({
      title: "Unable to load diseases",
      description: (error as any)?.message || "Please try again.",
      variant: "destructive",
    });
  }, [error, isError, toast]);

  const diseases = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasPrevious = Boolean(data?.previous);
  const hasNext = Boolean(data?.next);
  const tableColumnCount = visibleColumns.size + 2;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Card className="flex min-h-0 flex-1 flex-col">
        <CardHeader className="gap-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle className="z-h1 text-foreground">
                Manage Diseases
              </CardTitle>
              <CardDescription className="z-secondary text-muted-foreground">
                Create and view master diseases available across the website.
              </CardDescription>
            </div>
            <Button onClick={onAdd}>
              <Plus className="h-4 w-4" />
              Add Disease
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <SearchInput
                placeholder="Search diseases..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onClear={() => setSearchInput("")}
                isLoading={isFetching}
                containerClassName="w-full md:w-[360px]"
              />
              <label className="flex items-center gap-2 z-secondary text-muted-foreground">
                <Checkbox
                  checked={widelyPrevalentOnly}
                  onCheckedChange={(checked) =>
                    setListState({ prevalent: Boolean(checked) })
                  }
                />
                Widely prevalent
              </label>
              <label className="flex items-center gap-2 z-secondary text-muted-foreground">
                <Checkbox
                  checked={familyLinkedOnly}
                  onCheckedChange={(checked) =>
                    setListState({ family: Boolean(checked) })
                  }
                />
                Family linked
              </label>
              <label className="flex items-center gap-2 z-secondary text-muted-foreground">
                <Checkbox
                  checked={featuredOnly}
                  onCheckedChange={(checked) =>
                    setListState({ featured: Boolean(checked) })
                  }
                />
                Featured
              </label>
            </div>

            <ColumnsMenu
              columns={DISEASE_COLUMNS}
              visible={visibleColumns}
              onToggle={toggleColumn}
            />
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="mb-3 z-caption text-muted-foreground">
            Showing {diseases.length} of {totalCount} diseases
            {debouncedSearch && ` for "${debouncedSearch}"`}
          </div>

          <div className="flex-1 overflow-auto rounded-lg border">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card">
                <TableRow>
                  <TableHead className="z-eyebrow min-w-[320px] text-muted-foreground">
                    Disease
                  </TableHead>
                  {DISEASE_COLUMNS.map((column) =>
                    visibleColumns.has(column.key) ? (
                      <TableHead
                        key={column.key}
                        className="z-eyebrow text-muted-foreground"
                      >
                        {column.label}
                      </TableHead>
                    ) : null
                  )}
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableStateRow
                    colSpan={tableColumnCount}
                    state="loading"
                    message="Loading diseases…"
                  />
                ) : isError ? (
                  <TableStateRow
                    colSpan={tableColumnCount}
                    state="error"
                    message="Couldn't load diseases — the server didn't answer in time."
                    onRetry={() => refetch()}
                  />
                ) : diseases.length === 0 ? (
                  <TableStateRow
                    colSpan={tableColumnCount}
                    state="empty"
                    message="No diseases found"
                  />
                ) : (
                  diseases.map((disease) => (
                    <DiseaseTableRow
                      key={disease.id}
                      disease={disease}
                      visibleColumns={visibleColumns}
                      onView={() => onView(disease.id)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-between">
              <div className="z-caption text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={!hasPrevious || isLoading || isFetching}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!hasNext || isLoading || isFetching}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
